import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { MONITORING_PATHS } from "./nav-config";

const STORAGE_KEY = "vds.sidebarCollapsed";

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredCollapsed(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? "true" : "false");
  } catch {
    // No-op — storage unavailable (e.g. private mode).
  }
}

export interface SidebarCollapseState {
  /** Effective collapsed state. Forced to false on non-monitoring routes. */
  collapsed: boolean;
  /** True when the current path is a monitoring route. */
  canCollapse: boolean;
  /** Toggle collapse state. No-op on non-monitoring routes. */
  toggle: () => void;
}

/**
 * Manages sidebar collapse state per the redesign rules:
 *  - Persisted in localStorage under `vds.sidebarCollapsed`.
 *  - Collapse is only allowed on monitoring routes; everywhere else the
 *    sidebar is force-expanded so dense pages keep their nav labels.
 */
export function useSidebarCollapse(): SidebarCollapseState {
  const { pathname } = useLocation();
  const canCollapse = MONITORING_PATHS.some((p) => pathname.startsWith(p));

  const [stored, setStored] = useState<boolean>(readStoredCollapsed);

  // Sync to localStorage whenever the stored preference changes.
  useEffect(() => {
    writeStoredCollapsed(stored);
  }, [stored]);

  const toggle = useCallback(() => {
    if (!canCollapse) return;
    setStored((prev) => !prev);
  }, [canCollapse]);

  return {
    collapsed: canCollapse ? stored : false,
    canCollapse,
    toggle,
  };
}
